import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Book, 
  TrendingUp, 
  BarChart3, 
  Lightbulb, 
  Zap,
  Search,
  X,
  Filter,
  Download,
  Lock,
  FileText,
  Eye,
  Folder,
  Star,
  Crown,
  MessageCircle,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './Documents.css';

const Documents = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeCategory, setActiveCategory] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showVipModal, setShowVipModal] = useState(false);
  const [customerServiceUrl, setCustomerServiceUrl] = useState('');
  const { isAuthenticated, isVip } = useAuth();
  const navigate = useNavigate();

  const categories = [
    { id: 'strategies', icon: TrendingUp, title: 'Trading Strategies', description: 'Advanced trading methodologies', color: 'green' },
    { id: 'analysis', icon: BarChart3, title: 'Market Analysis', description: 'Technical and fundamental analysis', color: 'blue' },
    { id: 'guides', icon: Lightbulb, title: 'Beginner Guides', description: 'Start your trading journey', color: 'gold' },
    { id: 'references', icon: Zap, title: 'Quick References', description: 'Cheat sheets and summaries', color: 'blue' },
  ];

  useEffect(() => {
    fetchDocuments();
    fetchCustomerServiceUrl();
  }, []);

  const fetchCustomerServiceUrl = async () => {
    try {
      const response = await api.get('/settings/customerServiceUrl');
      if (response.data.success && response.data.data) {
        setCustomerServiceUrl(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch customer service URL:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/documents', { params: { limit: 20 } });
      if (response.data.success) {
        setDocuments(response.data.data.documents);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const filters = [
    { id: 'all', label: 'All', icon: Folder },
    { id: 'free', label: 'Free', icon: Star },
    { id: 'vip', label: 'VIP', icon: Lock },
  ];

  const filteredDocuments = documents.filter(doc => {
    const matchesFilter = activeFilter === 'all' || 
      (activeFilter === 'free' && !doc.isVip) ||
      (activeFilter === 'vip' && doc.isVip);
    const matchesCategory = !activeCategory || doc.category === activeCategory;
    const matchesSearch = !searchTerm || 
      doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesCategory && matchesSearch;
  });

  const handleDownload = async (doc) => {
    if (doc.isVip && !isVip) {
      // Non-VIP users cannot download VIP documents, show upgrade modal
      setShowVipModal(true);
      return;
    }
    try {
      const response = await api.post(`/documents/${doc.id}/download`);
      if (response.data.success && response.data.data.downloadUrl) {
        window.open(response.data.data.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Download failed:', error);
      // If backend returns VIP permission error, show modal
      if (error.response?.status === 403) {
        setShowVipModal(true);
      }
    }
  };

  // Contact customer service to activate VIP
  const handleContactSupport = () => {
    // Prioritize opening SaleSmartly chat window
    try {
      if (window.ssq && typeof window.ssq.open === 'function') {
        window.ssq.open();
        setShowVipModal(false);
        return;
      }
      if (window.SaleSmartly && typeof window.SaleSmartly.open === 'function') {
        window.SaleSmartly.open();
        setShowVipModal(false);
        return;
      }
      if (window.ssq && window.ssq.push) {
        window.ssq.push(['open']);
        setShowVipModal(false);
        return;
      }
    } catch (error) {
      console.error('Failed to open SaleSmartly chat:', error);
    }
    
    // If SaleSmartly is not available, use backend configured customer service link
    if (customerServiceUrl) {
      window.open(customerServiceUrl, '_blank');
      setShowVipModal(false);
      return;
    }
    
    // If none are configured, navigate to VIP page
    navigate('/vip');
    setShowVipModal(false);
  };

  // Handle VIP upgrade
  const handleUpgradeVip = () => {
    setShowVipModal(false);
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/documents' } });
    } else {
      navigate('/vip');
    }
  };

  const getCategoryIcon = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (category) {
      const IconComponent = category.icon;
      return <IconComponent size={24} />;
    }
    return <FileText size={24} />;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.06 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } }
  };

  if (loading) {
    return (
      <div className="docs-page docs-page--loading premium-page">
        <div className="docs-page__loader">
          <div className="docs-page__loader-spinner"></div>
          <div className="docs-page__loader-text">Loading resources...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="docs-page premium-page">
      
      {/* Background */}
      <div className="docs-page__bg" />

      <motion.div 
        className="docs-page__content"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Hero Section */}
        <motion.section className="docs-hero" variants={itemVariants}>
          <div className="docs-hero__badge">
            <Book size={16} />
            <span>Knowledge Center</span>
          </div>
          
          <h1 className="docs-hero__title">Trading Resources</h1>
          <p className="docs-hero__subtitle">
            Master the markets with our comprehensive library of guides, strategies, and expert analysis
          </p>
          
          {/* Search Bar */}
          <div className="docs-hero__search">
            <Search size={20} className="docs-hero__search-icon" />
            <input 
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="docs-hero__search-input"
            />
            {searchTerm && (
              <button className="docs-hero__search-clear" onClick={() => setSearchTerm('')}>
                <X size={16} />
              </button>
            )}
          </div>

          {/* Stats */}
          <div className="docs-hero__stats">
            <div className="docs-hero__stat">
              <span className="docs-hero__stat-value">{documents.length}</span>
              <span className="docs-hero__stat-label">Documents</span>
            </div>
            <div className="docs-hero__stat-divider"></div>
            <div className="docs-hero__stat">
              <span className="docs-hero__stat-value">{documents.filter(d => d.isVip).length}</span>
              <span className="docs-hero__stat-label">VIP Exclusive</span>
            </div>
            <div className="docs-hero__stat-divider"></div>
            <div className="docs-hero__stat">
              <span className="docs-hero__stat-value">{documents.reduce((a, d) => a + (d.downloads || 0), 0).toLocaleString()}</span>
              <span className="docs-hero__stat-label">Downloads</span>
            </div>
          </div>
        </motion.section>

        {/* Categories */}
        <motion.section className="docs-categories" variants={itemVariants}>
          <div className="docs-categories__grid">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <motion.button
                  key={category.id}
                  className={`docs-category ${activeCategory === category.id ? 'docs-category--active' : ''} docs-category--${category.color}`}
                  onClick={() => setActiveCategory(activeCategory === category.id ? null : category.id)}
                  whileHover={{ scale: 1.02, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="docs-category__icon">
                    <IconComponent size={28} />
                  </div>
                  <div className="docs-category__info">
                    <h3 className="docs-category__title">{category.title}</h3>
                    <p className="docs-category__desc">{category.description}</p>
                  </div>
                  <span className="docs-category__count">
                    {documents.filter(d => d.category === category.id).length}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </motion.section>

        {/* Toolbar */}
        <motion.div className="docs-toolbar" variants={itemVariants}>
          <div className="docs-toolbar__filters">
            {filters.map(filter => {
              const IconComponent = filter.icon;
              return (
                <button
                  key={filter.id}
                  className={`docs-toolbar__filter ${activeFilter === filter.id ? 'docs-toolbar__filter--active' : ''}`}
                  onClick={() => setActiveFilter(filter.id)}
                >
                  <IconComponent size={16} />
                  <span>{filter.label}</span>
                </button>
              );
            })}
          </div>
          
          <div className="docs-toolbar__info">
            {activeCategory && (
              <button className="docs-toolbar__clear" onClick={() => setActiveCategory(null)}>
                <X size={14} /> Clear Category
              </button>
            )}
            <span className="docs-toolbar__count">
              Showing <strong>{filteredDocuments.length}</strong> documents
            </span>
          </div>
        </motion.div>

        {/* Documents Grid */}
        <motion.section className="docs-grid" variants={containerVariants}>
          {filteredDocuments.length === 0 ? (
            <motion.div className="docs-empty" variants={itemVariants}>
              <Filter size={48} />
              <h3>No documents found</h3>
              <p>Try adjusting your filters or search term</p>
            </motion.div>
          ) : (
            filteredDocuments.map((doc) => (
              <motion.article 
                key={doc.id} 
                className={`docs-card ${doc.isVip ? 'docs-card--vip' : ''}`}
                variants={itemVariants}
                whileHover={{ y: -6 }}
              >
                <div className="docs-card__header">
                  <div className="docs-card__icon-box">
                    {getCategoryIcon(doc.category)}
                  </div>
                  <div className="docs-card__header-meta">
                    {doc.isVip && (
                      <div className="docs-card__vip-tag">
                        {isVip ? <Crown size={12} /> : <Lock size={12} />} VIP
                      </div>
                    )}
                    <span className="docs-card__category">
                      {categories.find(c => c.id === doc.category)?.title || 'General'}
                    </span>
                  </div>
                </div>

                <div className="docs-card__body">
                  <h3 className="docs-card__title">{doc.title}</h3>
                  <p className="docs-card__desc">{doc.description}</p>
                </div>
                
                <div className="docs-card__meta">
                  <div className="docs-card__meta-item">
                    <FileText size={14} /> {doc.pages || 0} pages
                  </div>
                  <div className="docs-card__meta-item">
                    <Eye size={14} /> {(doc.downloads || 0).toLocaleString()}
                  </div>
                </div>

                <button 
                  className={`docs-card__btn ${doc.isVip && !isVip ? 'docs-card__btn--vip' : ''}`}
                  onClick={() => handleDownload(doc)}
                >
                  {doc.isVip && !isVip ? (
                    <><Lock size={16} /> VIP Access</>
                  ) : (
                    <><Download size={16} /> Download</>
                  )}
                </button>
              </motion.article>
            ))
          )}
        </motion.section>
      </motion.div>

      {/* VIP Upgrade Modal */}
      {showVipModal && (
        <div className="docs-vip-modal-overlay" onClick={() => setShowVipModal(false)}>
          <motion.div 
            className="docs-vip-modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <button className="docs-vip-modal__close" onClick={() => setShowVipModal(false)}>
              <X size={20} />
            </button>
            
            <div className="docs-vip-modal__icon">
              <Crown size={48} />
            </div>
            
            <h2 className="docs-vip-modal__title">VIP Member Exclusive Content</h2>
            
            <p className="docs-vip-modal__desc">
              This document is exclusive to VIP members. Please contact customer service to activate VIP membership to download.
            </p>
            
            <div className="docs-vip-modal__benefits">
              <div className="docs-vip-modal__benefit">
                <FileText size={16} />
                <span>Unlock All VIP Documents</span>
              </div>
              <div className="docs-vip-modal__benefit">
                <Download size={16} />
                <span>Unlimited Downloads</span>
              </div>
              <div className="docs-vip-modal__benefit">
                <Star size={16} />
                <span>Exclusive Trading Strategies</span>
              </div>
            </div>
            
            <div className="docs-vip-modal__actions">
              <button 
                className="docs-vip-modal__btn docs-vip-modal__btn--secondary" 
                onClick={() => setShowVipModal(false)}
              >
                Maybe Later
              </button>
              <button 
                className="docs-vip-modal__btn docs-vip-modal__btn--primary" 
                onClick={handleContactSupport}
              >
                <MessageCircle size={16} />
                Contact Support to Activate VIP
                <ExternalLink size={14} />
              </button>
            </div>
            
            <p className="docs-vip-modal__note">
              Or go to <span onClick={handleUpgradeVip} style={{ color: 'var(--vip-gold)', cursor: 'pointer', textDecoration: 'underline' }}>VIP Center</span> to activate with activation code
            </p>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Documents;
